import {
  WEEKDAY_CODE_BY_INDEX,
  WEEKDAY_OPTIONS,
  type AvailabilityUserRef,
  type SlotSettings,
} from "@/types/availability";

/**
 * Client-side projection of the slots a set of viewing settings would generate.
 *
 * This is a *preview*, not a query: `/api/booking/v1/slots/available` reads the
 * settings already persisted for a real property, so it cannot answer "what
 * would these unsaved edits do". The rules below mirror the booking service's
 * slicing (booking-service-architecture.md §3.2) closely enough to tell the
 * operator whether a save produces slots at all, and roughly how many.
 *
 * Deliberately pure and React-free: the verdict panel, the KPI row and the 3D
 * scene all read the same projection, and it is cheap enough to recompute on
 * every keystroke.
 */

/** A single projected bookable slot. */
export interface ProjectedSlot {
  /** Minutes from midnight the viewing starts at. */
  startMinutes: number;
  /** Minutes from midnight the viewing ends at. */
  endMinutes: number;
  /** Index into {@link ProjectedDay.date}'s week, 0 = Monday. */
  dayIndex: number;
  /** Absolute start, in the browser's timezone — used for the notice cutoff. */
  start: Date;
  /** How many contacts may book it. */
  capacity: number;
  /** Agent this slot is assigned to, round-robin down the priority list. */
  user: AvailabilityUserRef | null;
  /** Position of {@link user} in the priority list, or -1 when unassigned. */
  userIndex: number;
  /** First slot of its day's block — the one the travel lead-in sits before. */
  firstOfBlock: boolean;
  /** Why the slot is not bookable, or null when it is. */
  blocked: SlotBlockReason | null;
}

export type SlotBlockReason = "notice" | "horizon" | "past";

export interface ProjectedDay {
  date: Date;
  /** MON…SUN. */
  weekdayCode: string;
  weekdayLabel: string;
  /** False when the recurrence does not run on this weekday — no slots at all. */
  active: boolean;
  slots: ProjectedSlot[];
  /** Slots on this day that a contact could actually book. */
  bookableCount: number;
}

/** A reason the settings produce nothing, worded for the operator. */
export interface BlockingIssue {
  /** Stable id so the UI can link the issue back to the field it is about. */
  id:
    | "scope"
    | "inactive"
    | "window-missing"
    | "window-order"
    | "duration-missing"
    | "interval-missing"
    | "capacity-missing"
    | "duration-overruns"
    | "interval-overruns"
    | "recurrence-missing"
    | "days-missing"
    | "users-missing"
    | "notice-exceeds-horizon"
    | "own-availability";
  message: string;
  /**
   * `error` stops slots being created; `warning` still generates them; `info`
   * explains something the operator should know but need not act on.
   */
  severity: "error" | "warning" | "info";
}

/**
 * One step of the "where did my slots go" waterfall. `removed` is how many slots
 * the rule takes off the running total; the final entry is what survives.
 */
export interface LossStep {
  id: "theoretical" | "days" | "notice" | "horizon" | "generated";
  label: string;
  /** Slots removed by this rule — 0 on the opening and closing rows. */
  removed: number;
  /** Running total after the rule has been applied. */
  running: number;
}

export interface SlotProjection {
  days: ProjectedDay[];
  /** Bookable slots in the previewed week. */
  weeklySlots: number;
  /** Bookable slots × leads per slot, for the previewed week. */
  weeklyCapacity: number;
  /**
   * Bookable slots across the whole generation horizon that these *rules*
   * describe — independent of whether the scope has them switched on.
   */
  horizonSlots: number;
  horizonCapacity: number;
  /**
   * What saving actually produces. Zero whenever the settings are inactive: the
   * saved row exists but the booking service ignores it and the next scope up
   * applies instead. Everything the operator reads as an outcome uses this;
   * {@link horizonSlots} is only ever shown as "would generate, once active".
   */
  generatedSlots: number;
  generatedCapacity: number;
  /** Days the horizon actually covers (capped by the horizon setting). */
  horizonDays: number;
  /** Distinct agents the projection hands slots to. */
  assignedUsers: number;
  /** Earliest bookable slot from `now`, anywhere in the horizon. */
  nextSlot: { start: Date; user: AvailabilityUserRef | null } | null;
  /** Everything standing between these settings and a generated slot. */
  issues: BlockingIssue[];
  /** True when nothing bookable comes out of these settings. */
  empty: boolean;
  /** Minutes from midnight the previewed grid spans, for the scene's floor plate. */
  windowStartMinutes: number | null;
  windowEndMinutes: number | null;
  /**
   * Every slot the window could theoretically hold, then each rule's cost, in
   * the order the booking service applies them. Answers "where did they go?" —
   * the question the issue list can't.
   */
  losses: LossStep[];
  /** Slot start times shared by every active day, for the flat views. */
  dailyStarts: number[];
  /** Whether the settings are marked active — the scene renders ghosted when not. */
  active: boolean;
  /**
   * The scope keeps its own availability, so the default window here is never
   * read and there is nothing for this preview to project from — the real slots
   * come from the property's own entry, which the settings API does not return.
   */
  windowNotApplicable: boolean;
}

/** Server fallback when no horizon is saved (SlotSettingsController's default). */
const FALLBACK_HORIZON_DAYS = 30;
const MINUTES_PER_DAY = 24 * 60;

/** "HH:mm" → minutes from midnight, or null when unparseable/blank. */
export function parseTimeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Minutes from midnight → "9:30 AM", matching the rest of the console. */
export function formatMinutes(total: number): string {
  const h24 = Math.floor(total / 60) % 24;
  const mins = total % 60;
  const suffix = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(mins).padStart(2, "0")} ${suffix}`;
}

/** Monday 00:00 of the week `date` falls in. */
export function startOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // getDay() is Sunday-first; the console is Monday-first everywhere else.
  const shift = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - shift);
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

const WEEKDAY_LABEL_BY_CODE = new Map(WEEKDAY_OPTIONS.map((d) => [d.code, d.label]));

/**
 * Which weekdays the recurrence runs on. DAILY is every day; WEEKLY is exactly
 * the days picked. A null recurrence type means the operator has not chosen yet
 * — no days, which is what makes the projection empty rather than optimistic.
 */
function activeWeekdays(settings: SlotSettings): Set<string> {
  if (settings.defaultRecurrenceType === "DAILY") return new Set(WEEKDAY_CODE_BY_INDEX);
  if (settings.defaultRecurrenceType === "WEEKLY") {
    return new Set(settings.defaultRecurrenceDays ?? []);
  }
  return new Set();
}

/**
 * Distance between two consecutive slot starts.
 *
 * An interval of 0 means back-to-back viewings — the booking service's slicer
 * moves the cursor on by the duration in that case rather than standing still,
 * so a zero interval is a valid rule and not a missing one.
 */
function stepMinutes(duration: number, interval: number): number {
  return interval > 0 ? interval : duration;
}

/**
 * Slot start times, in minutes from midnight, for one active day.
 *
 * The travel top-up is a lead-in *before* the first slot of the block, so the
 * first viewing starts that many minutes after the window opens; every slot
 * after it steps by {@link stepMinutes}. A slot must finish by the window end —
 * the window end is exclusive, no slot may start at or after it.
 */
function slotStartsForDay(
  windowStart: number,
  windowEnd: number,
  duration: number,
  interval: number,
  travel: number,
): number[] {
  const starts: number[] = [];
  if (duration <= 0 || windowEnd <= windowStart) return starts;

  const step = stepMinutes(duration, interval);
  let cursor = windowStart + travel;
  // A pathological interval (say 1 minute across a 12-hour window) would still
  // terminate, but there is no point projecting thousands of slots the operator
  // will never look at — the caller caps what it renders, this caps the maths.
  const maxSlotsPerDay = Math.ceil(MINUTES_PER_DAY / Math.max(step, 1)) + 1;
  while (cursor + duration <= windowEnd && starts.length < maxSlotsPerDay) {
    starts.push(cursor);
    cursor += step;
  }
  return starts;
}

export interface ProjectionInput {
  settings: SlotSettings;
  /** Monday of the week to lay out. Defaults to the current week. */
  weekStart?: Date;
  /** Injected so the notice cutoff is testable. */
  now?: Date;
  /** Scope still on "All" — nothing can be saved, so nothing is projected. */
  scopeIncomplete?: boolean;
  /** The saved `activeFlag`; when off the rules exist but are ignored. */
  activeFlag?: boolean;
  /**
   * The selected scope already has its own availability entry. The default
   * window stops being required, and an unset one stops being an error.
   */
  ownAvailability?: boolean;
}

/**
 * Projects a week of slots plus horizon-wide totals from the given settings.
 * Always returns a shape the UI can render — an unfinished form yields empty
 * days and a populated {@link SlotProjection.issues} list.
 */
export function projectSlots({
  settings,
  weekStart,
  now = new Date(),
  scopeIncomplete = false,
  activeFlag = true,
  ownAvailability = false,
}: ProjectionInput): SlotProjection {
  const anchor = startOfWeek(weekStart ?? now);
  const duration = settings.slotDuration ?? 0;
  const interval = settings.slotInterval ?? 0;
  const travel = settings.topupTravelTime ?? 0;
  const capacity = settings.leadsPerSlot ?? 0;
  const users = settings.users ?? [];
  // The rotation the preview actually cycles. With the round-robin off (the default) one agent
  // takes every viewing -- the highest-priority one, which is the head of this list -- so the
  // rotation is cut to that single agent and every projected slot names them. On, the whole list
  // cycles. This mirrors what the booking service does with the same flag; the preview would
  // otherwise show viewings spread across the team that the server never spreads.
  const rotation = settings.roundRobinEnabled ? users : users.slice(0, 1);
  const windowStart = parseTimeToMinutes(settings.defaultWindowStart);
  const windowEnd = parseTimeToMinutes(settings.defaultWindowEnd);
  const horizonDays = settings.generationHorizonDays ?? FALLBACK_HORIZON_DAYS;
  const noticeHours = settings.minNoticeHours ?? 0;

  const issues = collectIssues({
    settings,
    scopeIncomplete,
    activeFlag,
    windowStart,
    windowEnd,
    duration,
    interval,
    travel,
    capacity,
    horizonDays,
    noticeHours,
    ownAvailability,
  });
  const fatal = issues.some((i) => i.severity === "error");

  // Nothing to draw: the window is blank because the scope brings its own, and
  // this preview cannot read that entry.
  const windowNotApplicable =
    ownAvailability && (windowStart == null || windowEnd == null || weekdaysUnset(settings));

  const weekdays = activeWeekdays(settings);
  const noticeCutoff = new Date(now.getTime() + noticeHours * 60 * 60 * 1000);
  const horizonEnd = addDays(startOfDay(now), horizonDays);

  // The round-robin runs across the whole horizon, not per day, so agent N+1 picks
  // up where the previous day left off — same as the booking service walking the
  // priority list. Counted from the horizon start so the previewed week shows the
  // assignment it would really get. With the round-robin off the ordinal still
  // advances, but `rotation` has one entry so it always lands on the same agent.
  const startsPerActiveDay =
    fatal || windowStart == null || windowEnd == null
      ? []
      : slotStartsForDay(windowStart, windowEnd, duration, interval, travel);

  const days: ProjectedDay[] = [];
  for (let i = 0; i < 7; i += 1) {
    const date = addDays(anchor, i);
    const weekdayCode = WEEKDAY_CODE_BY_INDEX[date.getDay()];
    const active = weekdays.has(weekdayCode) && !fatal;
    const slots: ProjectedSlot[] = [];

    if (active) {
      startsPerActiveDay.forEach((startMinutes, idx) => {
        const start = new Date(date);
        start.setHours(0, startMinutes, 0, 0);
        const blocked = blockReason(start, now, noticeCutoff, horizonEnd);
        // Assignment follows the horizon-wide slot ordinal so the rotation is
        // stable as the operator pages between weeks.
        const ordinal = horizonOrdinal(startOfDay(now), date, idx, startsPerActiveDay.length);
        const userIndex = rotation.length
          ? ((ordinal % rotation.length) + rotation.length) % rotation.length
          : -1;
        slots.push({
          startMinutes,
          endMinutes: startMinutes + duration,
          dayIndex: i,
          start,
          capacity,
          user: userIndex >= 0 ? rotation[userIndex] : null,
          userIndex,
          firstOfBlock: idx === 0,
          blocked,
        });
      });
    }

    days.push({
      date,
      weekdayCode,
      weekdayLabel: WEEKDAY_LABEL_BY_CODE.get(weekdayCode) ?? weekdayCode,
      active,
      slots,
      bookableCount: slots.filter((s) => s.blocked == null).length,
    });
  }

  const weeklySlots = days.reduce((sum, d) => sum + d.bookableCount, 0);

  // Horizon totals count every active weekday between now and the horizon end,
  // which is what the operator is really saving — the week grid is just a window
  // onto it.
  const horizon = projectHorizonTotals({
    now,
    horizonDays,
    horizonEnd,
    weekdays,
    startsPerActiveDay,
    noticeCutoff,
  });

  const assignedUsers = fatal ? 0 : Math.min(rotation.length, horizon.slots || rotation.length);
  const generatedSlots = activeFlag ? horizon.slots : 0;

  const losses = buildLosses({
    startsPerDay: startsPerActiveDay.length,
    weekdayCount: weekdays.size,
    horizonDays,
    bookable: horizon.slots,
    generated: generatedSlots,
    activeFlag,
  });

  return {
    days,
    weeklySlots: activeFlag ? weeklySlots : 0,
    weeklyCapacity: activeFlag ? weeklySlots * capacity : 0,
    horizonSlots: horizon.slots,
    horizonCapacity: horizon.slots * capacity,
    generatedSlots,
    generatedCapacity: generatedSlots * capacity,
    horizonDays,
    assignedUsers: generatedSlots > 0 ? assignedUsers : 0,
    // An inactive scope generates nothing, so there is no "next slot" to name.
    nextSlot: horizon.nextSlot && activeFlag
      ? {
          start: horizon.nextSlot,
          user: rotation.length
            ? rotation[
                ((horizon.nextSlotOrdinal % rotation.length) + rotation.length) % rotation.length
              ]
            : null,
        }
      : null,
    issues,
    empty: generatedSlots === 0,
    windowStartMinutes: windowStart,
    windowEndMinutes: windowEnd,
    losses,
    dailyStarts: startsPerActiveDay,
    active: activeFlag,
    windowNotApplicable,
  };
}

/** No recurrence chosen, or a weekly one with no days on it. */
function weekdaysUnset(settings: SlotSettings): boolean {
  if (settings.defaultRecurrenceType == null) return true;
  return (
    settings.defaultRecurrenceType === "WEEKLY" &&
    (settings.defaultRecurrenceDays?.length ?? 0) === 0
  );
}

/**
 * The loss waterfall. Each rule is charged for what it removes from the running
 * total, in the order the booking service applies them, so the operator can see
 * which rule is expensive rather than only that something is wrong.
 */
function buildLosses({
  startsPerDay,
  weekdayCount,
  horizonDays,
  bookable,
  generated,
  activeFlag,
}: {
  startsPerDay: number;
  weekdayCount: number;
  horizonDays: number;
  bookable: number;
  generated: number;
  activeFlag: boolean;
}): LossStep[] {
  // The ceiling is the window running every single day of the horizon.
  const theoretical = startsPerDay * horizonDays;
  const afterDays = Math.round((theoretical * weekdayCount) / 7);
  const lostToDays = theoretical - afterDays;
  // Whatever the day maths does not explain was removed by the notice period —
  // deriving it this way keeps the waterfall reconciled with the real count.
  const lostToNotice = Math.max(0, afterDays - bookable);

  const steps: LossStep[] = [
    { id: "theoretical", label: "Window could hold", removed: 0, running: theoretical },
    { id: "days", label: "Days not selected", removed: lostToDays, running: afterDays },
    { id: "notice", label: "Minimum notice", removed: lostToNotice, running: bookable },
  ];

  if (!activeFlag) {
    steps.push({ id: "horizon", label: "Settings inactive", removed: bookable, running: 0 });
  }
  steps.push({ id: "generated", label: "Generated", removed: 0, running: generated });
  return steps;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Whole days between two midnights. */
function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000);
}

/**
 * Position of a slot in the horizon-wide sequence, so the round-robin does not
 * restart every week the operator pages to.
 */
function horizonOrdinal(
  horizonStart: Date,
  date: Date,
  slotIndex: number,
  slotsPerDay: number,
): number {
  return daysBetween(horizonStart, date) * slotsPerDay + slotIndex;
}

function blockReason(
  start: Date,
  now: Date,
  noticeCutoff: Date,
  horizonEnd: Date,
): SlotBlockReason | null {
  if (start < now) return "past";
  if (start < noticeCutoff) return "notice";
  if (start >= horizonEnd) return "horizon";
  return null;
}

/**
 * Bookable slots across the full horizon, plus the first one. Walks day by day
 * — the horizon is capped at 30 days upstream, so this is at most 30 iterations.
 */
function projectHorizonTotals({
  now,
  horizonDays,
  horizonEnd,
  weekdays,
  startsPerActiveDay,
  noticeCutoff,
}: {
  now: Date;
  horizonDays: number;
  horizonEnd: Date;
  weekdays: Set<string>;
  startsPerActiveDay: number[];
  noticeCutoff: Date;
}): { slots: number; nextSlot: Date | null; nextSlotOrdinal: number } {
  if (startsPerActiveDay.length === 0 || weekdays.size === 0) {
    return { slots: 0, nextSlot: null, nextSlotOrdinal: 0 };
  }

  const horizonStart = startOfDay(now);
  let slots = 0;
  let nextSlot: Date | null = null;
  let nextSlotOrdinal = 0;

  for (let dayOffset = 0; dayOffset < horizonDays; dayOffset += 1) {
    const date = addDays(horizonStart, dayOffset);
    if (!weekdays.has(WEEKDAY_CODE_BY_INDEX[date.getDay()])) continue;

    startsPerActiveDay.forEach((startMinutes, idx) => {
      const start = new Date(date);
      start.setHours(0, startMinutes, 0, 0);
      if (blockReason(start, now, noticeCutoff, horizonEnd) != null) return;
      slots += 1;
      if (nextSlot == null) {
        nextSlot = start;
        nextSlotOrdinal = horizonOrdinal(horizonStart, date, idx, startsPerActiveDay.length);
      }
    });
  }

  return { slots, nextSlot, nextSlotOrdinal };
}

/**
 * Everything standing between these settings and a generated slot, worded for
 * the operator and ordered the way the form reads. `error` means saving creates
 * nothing; `warning` means slots appear but something is probably not intended.
 */
function collectIssues({
  settings,
  scopeIncomplete,
  activeFlag,
  windowStart,
  windowEnd,
  duration,
  interval,
  travel,
  capacity,
  horizonDays,
  noticeHours,
  ownAvailability,
}: {
  settings: SlotSettings;
  scopeIncomplete: boolean;
  activeFlag: boolean;
  windowStart: number | null;
  windowEnd: number | null;
  duration: number;
  interval: number;
  travel: number;
  capacity: number;
  horizonDays: number;
  noticeHours: number;
  ownAvailability: boolean;
}): BlockingIssue[] {
  const issues: BlockingIssue[] = [];

  if (scopeIncomplete) {
    issues.push({
      id: "scope",
      severity: "error",
      message: "No scope selected — pick a branch or property above.",
    });
  }

  if (settings.slotDuration == null || duration <= 0) {
    issues.push({
      id: "duration-missing",
      severity: "error",
      message: "Slot duration is empty — a viewing needs a length.",
    });
  }
  // Only a blank field is a problem. `0` is a real rule — back-to-back slots,
  // one starting as the previous one ends — so it must not read as unfinished.
  if (settings.slotInterval == null || interval < 0) {
    issues.push({
      id: "interval-missing",
      severity: "error",
      message: "Slot interval is empty — use 0 for back-to-back slots, or a gap between starts.",
    });
  }

  if (ownAvailability && weekdaysUnset(settings)) {
    // The window is dead configuration for this scope, so its absence is a fact
    // to state rather than a problem to fix.
    issues.push({
      id: "own-availability",
      severity: "info",
      message:
        "This property has its own availability, so the default window is not used. The slot rules still apply to it.",
    });
  } else if (settings.defaultRecurrenceType == null) {
    issues.push({
      id: "recurrence-missing",
      severity: "error",
      message: "Repeat is not set — choose Daily, or Weekly and the days it runs on.",
    });
  } else if (
    settings.defaultRecurrenceType === "WEEKLY" &&
    (settings.defaultRecurrenceDays?.length ?? 0) === 0
  ) {
    issues.push({
      id: "days-missing",
      severity: "error",
      message: "Weekly repeat with no days selected — pick at least one weekday.",
    });
  }

  if (ownAvailability && (windowStart == null || windowEnd == null)) {
    // Covered by the note above; no second complaint about the same window.
  } else if (windowStart == null || windowEnd == null) {
    issues.push({
      id: "window-missing",
      severity: "error",
      message: "The availability window needs both a start and an end time.",
    });
  } else if (windowEnd <= windowStart) {
    issues.push({
      id: "window-order",
      severity: "error",
      message: "Window end is at or before window start — the window is empty.",
    });
  } else {
    const usable = windowEnd - windowStart - travel;
    if (duration > 0 && usable < duration) {
      issues.push({
        id: "duration-overruns",
        severity: "error",
        message:
          travel > 0
            ? `A ${duration} min viewing plus ${travel} min top-up does not fit the ${windowEnd - windowStart} min window.`
            : `A ${duration} min viewing does not fit the ${windowEnd - windowStart} min window.`,
      });
    } else if (interval > 0 && duration > 0 && interval > usable) {
      issues.push({
        id: "interval-overruns",
        severity: "warning",
        message: `A ${interval} min interval leaves room for only one slot a day.`,
      });
    }
  }

  if (settings.leadsPerSlot == null || capacity <= 0) {
    issues.push({
      id: "capacity-missing",
      severity: "error",
      message: "Leads per slot is empty — a slot with no capacity cannot be booked.",
    });
  }

  if ((settings.users?.length ?? 0) === 0) {
    issues.push({
      id: "users-missing",
      severity: "error",
      message: "No default users — generated slots would have nobody to assign.",
    });
  }

  if (noticeHours > 0 && noticeHours >= horizonDays * 24) {
    issues.push({
      id: "notice-exceeds-horizon",
      severity: "error",
      message: `A ${noticeHours}h minimum notice rules out every slot inside the ${horizonDays}-day horizon.`,
    });
  }

  if (!activeFlag && !scopeIncomplete) {
    issues.push({
      id: "inactive",
      severity: "warning",
      message: "Settings are inactive — they save, but the next scope up applies instead.",
    });
  }

  return issues;
}

/** Slot/capacity delta between the saved settings and the edited ones. */
export interface ProjectionDiff {
  slots: number;
  capacity: number;
  slotsDelta: number;
  capacityDelta: number;
  previousSlots: number;
  previousCapacity: number;
  /** No saved baseline to compare against (nothing persisted for this scope yet). */
  isFirstSave: boolean;
}

export function diffProjections(
  current: SlotProjection,
  previous: SlotProjection | null,
  hasSavedBaseline: boolean,
): ProjectionDiff {
  // Compared on what each version actually generates, so flipping the active
  // flag shows up in the diff as the change it really is.
  const previousSlots = previous?.generatedSlots ?? 0;
  const previousCapacity = previous?.generatedCapacity ?? 0;
  return {
    slots: current.generatedSlots,
    capacity: current.generatedCapacity,
    previousSlots,
    previousCapacity,
    slotsDelta: current.generatedSlots - previousSlots,
    capacityDelta: current.generatedCapacity - previousCapacity,
    isFirstSave: !hasSavedBaseline,
  };
}
